import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, AppThunk } from '@/app/types';
import { fetchReportData } from '../api/fetchReportData';
import { ReportType, ReportFilters } from '../types/reportTypes';

interface EventReportsState {
  currentReport: ReportType | null;
  data: Record<string, unknown>[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;

  fetchLoading: boolean;
  fetchError: string | null;

  exportLoading: boolean;
  exportError: string | null;
}

const initialState: EventReportsState = {
  currentReport: null,
  data: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  totalPages: 0,

  fetchLoading: false,
  fetchError: null,

  exportLoading: false,
  exportError: null,
};

export const eventReportsSlice = createSlice({
  name: 'eventReports',
  initialState,
  reducers: {
    setCurrentReport(state, action: PayloadAction<ReportType | null>) {
      state.currentReport = action.payload;
    },
    setData(state, action: PayloadAction<Record<string, unknown>[]>) {
      state.data = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
    },
    setTotalPages(state, action: PayloadAction<number>) {
      state.totalPages = action.payload;
    },
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setExportLoading(state, action: PayloadAction<boolean>) {
      state.exportLoading = action.payload;
    },
    setExportError(state, action: PayloadAction<string | null>) {
      state.exportError = action.payload;
    },
    clearReportData(state) {
      state.data = [];
      state.totalCount = 0;
      state.page = 1;
      state.totalPages = 0;
      state.fetchError = null;
    },
  },
});

export default eventReportsSlice.reducer;

export const {
  setCurrentReport,
  setData,
  setTotalCount,
  setPage,
  setPageSize,
  setTotalPages,
  setFetchLoading,
  setFetchError,
  setExportLoading,
  setExportError,
  clearReportData,
} = eventReportsSlice.actions;

export interface FetchReportParams {
  reportType: ReportType;
  filters: ReportFilters;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const fetchReportAction =
  (params: FetchReportParams): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));

    try {
      const result = await fetchReportData({
        reportType: params.reportType,
        filters: params.filters,
        page: params.page,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      });

      dispatch(setData(result.data));
      dispatch(setTotalCount(result.meta.totalCount));
      dispatch(setPage(result.meta.page));
      dispatch(setTotalPages(result.meta.totalPages));
      dispatch(setCurrentReport(params.reportType));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch report data';
      dispatch(setFetchError(message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };
